import { useCallback, useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { getMonthToDateRange } from '@/utils/date';
import { DATE_RANGE_FILTER_KEYS } from '@/utils/filterFields';
import type { DonationCandidateParams } from '../types/marketPromotionP2.types';
import {
  toDonationCandidateFilterSearch,
  toDonationCandidateFilterValues,
} from '../utils/mpFilters';

const route = getRouteApi('/_auth/market-promotion/publish-panel/new');

export function useDonationCandidateFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const defaultPeriod = useMemo(() => getMonthToDateRange(), []);
  const appliedValues = useMemo(
    () => toDonationCandidateFilterValues(search, defaultPeriod),
    [search, defaultPeriod],
  );

  const normalizeFilterValues = useCallback(
    (values: Record<string, string>) =>
      toDonationCandidateFilterValues(toDonationCandidateFilterSearch(values), defaultPeriod),
    [defaultPeriod],
  );

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    defaultValues: defaultPeriod,
    rangeKeys: DATE_RANGE_FILTER_KEYS,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...toDonationCandidateFilterSearch(values), q: previous.q }),
        replace: true,
      });
    },
  });

  const applySearchText = (text: string | undefined) => {
    void navigate({ search: (previous) => ({ ...previous, q: text }), replace: true });
  };

  const params = useMemo(
    (): DonationCandidateParams => ({
      promoItemId: search.promoItemId,
      dateFrom: search.dateFrom ?? defaultPeriod.dateFrom,
      dateTo: search.dateTo ?? defaultPeriod.dateTo,
    }),
    [search, defaultPeriod],
  );

  return { ...filters, params, appliedSearchText: search.q, applySearchText };
}
